import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { createClient, User, Session } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

// Check if Supabase is properly configured
const isSupabaseConfigured =
  supabaseUrl &&
  supabaseAnonKey &&
  supabaseUrl !== 'your_supabase_project_url_here' &&
  supabaseAnonKey !== 'your_supabase_anon_key_here' &&
  supabaseUrl.startsWith('http')

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: 'zypheron-website-auth-token',
      },
    })
  : null

interface UserPermissions {
  role: string
  features: Record<string, boolean>
  canBypassMFA: boolean
  canExtendSession: boolean
  hasUnlimitedTokens: boolean
  maxSessionHours: number
}

interface AuthContextType {
  user: User | null
  session: Session | null
  userPermissions: UserPermissions | null
  loading: boolean
  isSupabaseConfigured: boolean
  signIn: (email: string, password: string) => Promise<{ error?: { message?: string } | null; needsEmailConfirmation?: boolean }>
  signUp: (email: string, password: string, emailRedirectTo?: string) => Promise<{ error?: { message?: string } | null; needsEmailConfirmation?: boolean }>
  signInWithGitHub: (redirectTo?: string) => Promise<{ error?: { message?: string } | null }>
  signOut: () => Promise<{ success: boolean; error?: unknown }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

const getApiBaseUrl = (): string => {
  const configuredBaseUrl = import.meta.env.VITE_API_URL
  if (configuredBaseUrl) {
    if (typeof window !== 'undefined') {
      const isProdHost = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
      const configuredIsLocalhost = configuredBaseUrl.includes('localhost') || configuredBaseUrl.includes('127.0.0.1')
      if (isProdHost && configuredIsLocalhost) {
        return window.location.origin
      }
    }

    return configuredBaseUrl
  }

  if (typeof window !== 'undefined') {
    const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    if (isLocalHost) {
      return 'http://localhost:3001'
    }

    return window.location.origin
  }

  return 'http://localhost:3001'
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null)
  const [loading, setLoading] = useState(false)
  const syncedUserIdRef = useRef<string | null>(null)

  // Helper function to fetch user permissions
  const fetchUserPermissions = async (token: string) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUserPermissions(data.permissions)
      }
    } catch {
      // Failed to fetch user permissions
    }
  }

  const postJson = async (
    path: string,
    body: Record<string, unknown>,
    accessToken?: string
  ) => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }
    const response = await fetch(`${getApiBaseUrl()}/api${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Request failed' }))
      throw new Error(errorData.message || 'Request failed')
    }

    return response.json().catch(() => null)
  }

  // Server-side /register and /login routes are authenticated and bind to the
  // verified Supabase token (req.user.id). userId / email no longer travel in
  // the body. termsAccepted / privacyAccepted are forwarded only when the
  // caller actually collected the gesture from the user — defaulting to false
  // keeps fake compliance evidence out of the database.
  const syncRegisteredUser = async (
    accessToken: string,
    email: string,
    registrationSource = 'website_signup',
    consents: { terms?: boolean; privacy?: boolean } = {}
  ) => {
    await postJson('/user/register', {
      email,
      registrationSource,
      referrerUrl: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      intendedPlan: 'free',
      termsAccepted: consents.terms === true,
      privacyAccepted: consents.privacy === true,
    }, accessToken)
  }

  const recordUserLogin = async (accessToken: string) => {
    try {
      await postJson('/user/login', {}, accessToken)
    } catch {
      // Login telemetry should not block sign-in.
    }
  }

  const syncAuthenticatedUser = async (authUser: User, accessToken?: string) => {
    if (!authUser.id || !authUser.email || !accessToken) {
      return
    }

    if (syncedUserIdRef.current === authUser.id) {
      return
    }

    syncedUserIdRef.current = authUser.id

    try {
      const provider = authUser.app_metadata?.provider
      const registrationSource = provider === 'github'
        ? 'website_github_oauth'
        : 'website_signup'

      // Consents are intentionally NOT auto-asserted here — a session refresh
      // or OAuth round-trip is not a fresh gesture. Pass real consent flags
      // from the Signup form when this code path runs from a true new signup.
      await syncRegisteredUser(accessToken, authUser.email, registrationSource)
      await recordUserLogin(accessToken)
    } catch {
      syncedUserIdRef.current = null
    }
  }

  useEffect(() => {
    if (!supabase) {
      setLoading(false)
      return
    }

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.access_token) {
        await fetchUserPermissions(session.access_token)
      }
      if (session?.user && session?.access_token) {
        await syncAuthenticatedUser(session.user, session.access_token)
      }
      setLoading(false)
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)

      if (session?.user && session?.access_token) {
        await fetchUserPermissions(session.access_token)
        await syncAuthenticatedUser(session.user, session.access_token)
      } else {
        setUserPermissions(null)
        syncedUserIdRef.current = null
      }

      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    if (!supabase) {
      return { error: { message: 'Supabase is not configured for the website.' } }
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (!error && data.user) {
      await syncAuthenticatedUser(data.user, data.session?.access_token)
    }

    return { error }
  }

  const signUp = async (email: string, password: string, emailRedirectTo?: string) => {
    if (!supabase) {
      return { error: { message: 'Supabase is not configured for the website.' } }
    }

    const normalizedEmail = email.trim().toLowerCase()
    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        emailRedirectTo: emailRedirectTo || `${window.location.origin}/auth/confirmed?mode=ready`,
        data: {
          registration_source: 'website_signup',
        },
      },
    })

    if (error) {
      return { error, needsEmailConfirmation: false }
    }

    if (data.session) {
      await supabase.auth.signOut()
      return {
        error: {
          message: 'Email confirmation is not enabled for this project. Account creation is paused until verification is enforced.',
        },
        needsEmailConfirmation: false,
      }
    }

    return { error: null, needsEmailConfirmation: true }
  }

  const signInWithGitHub = async (redirectTo?: string) => {
    if (!supabase) {
      return { error: { message: 'Supabase is not configured for the website.' } }
    }

    const oauthRedirectTo = redirectTo || `${window.location.origin}/auth/confirmed?mode=signin`
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo: oauthRedirectTo,
      },
    })

    return { error }
  }

  const signOut = async () => {
    try {
      if (!supabase) {
        setUser(null)
        setSession(null)
        setUserPermissions(null)
        syncedUserIdRef.current = null
        return { success: true }
      }

      const { error } = await supabase.auth.signOut()
      if (error) {
        return { success: false, error }
      }

      setUser(null)
      setSession(null)
      setUserPermissions(null)
      syncedUserIdRef.current = null

      return { success: true }
    } catch (error) {
      return { success: false, error }
    }
  }

  const value: AuthContextType = {
    user,
    session,
    userPermissions,
    loading,
    isSupabaseConfigured: Boolean(isSupabaseConfigured),
    signIn,
    signUp,
    signInWithGitHub,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
} 
